import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiResponse } from '@evi/shared';
import { api } from '@/lib/api';
import styles from '../partner-jobs/PartnerJobs.module.css';

/**
 * EVI's human queues (Warren, 21 October 2026: "A person at EVI only sees exceptions. Every human queue has a timer that
 * escalates to every holder of that permission by email."): each queue, who holds it, and each item's age against the one
 * business day promise (P2.10). Super Admin (Api: routes/admin/evi-queues.ts, lib/eviQueues.ts).
 */
interface Queue {
  key: string; label: string; holders: string; promise_business_days: number; count: number; overdue: number; oldest_since: string | null;
  items: Array<{ id: string; title: string; detail: string; link: string; since: string; due_at: string; overdue: boolean }>;
}
const age = (since: string) => {
  const h = Math.max(0, Math.round((Date.now() - +new Date(since)) / 3_600_000));
  return h < 48 ? `${h} hour${h === 1 ? '' : 's'}` : `${Math.round(h / 24)} days`;
};

export function EviQueuesPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const q = useQuery({ queryKey: ['evi-queues'], refetchInterval: 60_000, retry: false,
    queryFn: () => api.get('admin/evi-queues', { retry: 0 }).json<ApiResponse<{ queues: Queue[] }>>().then(r => r.data!.queues) });
  // Today's rows open their queue here by its key (Warren, 21 October 2026, item M).
  const loaded = !!q.data;
  useEffect(() => {
    const key = window.location.hash.slice(1);
    if (loaded && /^[a-z_]+$/.test(key)) document.getElementById(`queue-${key}`)?.scrollIntoView({ block: 'start' });
  }, [loaded]);
  if (q.error) return <main className={styles.page}><p role="alert">EVI queues are for Super Admins.</p></main>;
  if (!q.data) return <main className={styles.page}><p className={styles.message}>Loading the queues.</p></main>;
  const waiting = q.data.reduce((n, x) => n + x.count, 0), late = q.data.reduce((n, x) => n + x.overdue, 0);
  return <main className={styles.page}>
    <div className={styles.heading}><div>
      <h1 className={styles.title}>EVI queues</h1>
      <p className={styles.lead}>What waits on a person at EVI. Every step promises one business day (Monday to Friday, US federal holidays excluded, in ET);
        past it, every holder of the queue&rsquo;s permission is emailed. {waiting} waiting, {late} past the promise.</p>
    </div></div>
    {q.data.map(queue => <section key={queue.key} id={`queue-${queue.key}`} className={styles.card}>
      <div className={styles.cardHead}><h2 className={styles.kicker}>{queue.label}</h2>
        <span className={styles.slotTag} data-tone={queue.overdue ? 'warn' : queue.count ? undefined : 'good'}>{queue.count ? `${queue.count} waiting${queue.overdue ? `, ${queue.overdue} late` : ''}` : 'Clear'}</span></div>
      <p className={styles.hint}>Held by {queue.holders}. Promise: {queue.promise_business_days} business day.</p>
      {queue.items.map(i => <div key={i.id} className={styles.slot}>
        <div>
          <div className={styles.strong}><a href={i.link} onClick={e => { e.preventDefault(); onNavigate(i.link); }}>{i.title}</a></div>
          <div className={styles.sub}>{[i.detail, `waiting ${age(i.since)}`, `promised by ${new Date(i.due_at).toLocaleString()}`].filter(Boolean).join(' · ')}</div>
        </div>
        <span className={styles.slotTag} data-tone={i.overdue ? 'warn' : undefined}>{i.overdue ? 'Past its promise' : 'On time'}</span>
      </div>)}
    </section>)}
  </main>;
}
